import { Language } from "@/i18n/translations";

// Re-export Language type for convenience
export type { Language };

/**
 * Complete route mapping for multilingual URLs
 * Following Google's SEO best practices with translated URLs
 */

export interface RouteMapping {
  en: string;
  es: string;
  vi: string;
}

// Main practice areas
export const practiceAreaRoutes: Record<string, RouteMapping> = {
  immigration: {
    en: "/practice-areas/immigration-services",
    es: "/es/areas-de-practica/servicios-de-inmigracion",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru"
  },
  criminal: {
    en: "/practice-areas/criminal-defense",
    es: "/es/areas-de-practica/defensa-criminal",
    vi: "/vi/linh-vuc-hanh-nghe/bao-ve-hinh-su"
  },
  family: {
    en: "/practice-areas/family-law",
    es: "/es/areas-de-practica/derecho-familiar",
    vi: "/vi/linh-vuc-hanh-nghe/luat-gia-dinh"
  },
  personalInjury: {
    en: "/practice-areas/personal-injury",
    es: "/es/areas-de-practica/lesiones-personales",
    vi: "/vi/linh-vuc-hanh-nghe/thuong-tich-ca-nhan"
  },
  realEstate: {
    en: "/practice-areas/real-estate-law",
    es: "/es/areas-de-practica/derecho-inmobiliario",
    vi: "/vi/linh-vuc-hanh-nghe/luat-bat-dong-san"
  },
  business: {
    en: "/practice-areas/business-law",
    es: "/es/areas-de-practica/derecho-empresarial",
    vi: "/vi/linh-vuc-hanh-nghe/luat-doanh-nghiep"
  },
  probate: {
    en: "/practice-areas/probate-estate",
    es: "/es/areas-de-practica/sucesiones-testamentos",
    vi: "/vi/linh-vuc-hanh-nghe/di-san-va-di-chuc"
  },
  tax: {
    en: "/practice-areas/tax-law",
    es: "/es/areas-de-practica/derecho-fiscal",
    vi: "/vi/linh-vuc-hanh-nghe/luat-thue"
  }
};

// Immigration sub-pages
export const immigrationRoutes: Record<string, RouteMapping> = {
  familyBased: {
    en: "/practice-areas/immigration-services/family-based-immigration",
    es: "/es/areas-de-practica/servicios-de-inmigracion/inmigracion-familiar",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/di-tru-gia-dinh"
  },
  employmentBased: {
    en: "/practice-areas/immigration-services/employment-based-immigration",
    es: "/es/areas-de-practica/servicios-de-inmigracion/inmigracion-laboral",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/di-tru-lao-dong"
  },
  k1Visa: {
    en: "/practice-areas/immigration-services/k-1-fiance-visa",
    es: "/es/areas-de-practica/servicios-de-inmigracion/visa-k1-prometido",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/visa-k1-hon-phu"
  },
  asylumPage: {
    en: "/practice-areas/immigration-services/asylum-in-the-united-states",
    es: "/es/areas-de-practica/servicios-de-inmigracion/asilo-estados-unidos",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/ti-nan-my"
  },
  citizenship: {
    en: "/practice-areas/immigration-services/citizenship-naturalization",
    es: "/es/areas-de-practica/servicios-de-inmigracion/ciudadania-naturalizacion",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/quoc-tich-nhap-tich"
  },
  deportation: {
    en: "/practice-areas/immigration-services/deportation-removal-defense",
    es: "/es/areas-de-practica/servicios-de-inmigracion/defensa-deportacion",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/bao-ve-truy-xuat"
  },
  waivers: {
    en: "/practice-areas/immigration-services/waivers-i601-i601a-i212",
    es: "/es/areas-de-practica/servicios-de-inmigracion/exenciones-i601",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/mien-tru-i601"
  },
  daca: {
    en: "/practice-areas/immigration-services/deferred-action-for-childhood-arrivals",
    es: "/es/areas-de-practica/servicios-de-inmigracion/accion-diferida-daca",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/hanh-dong-hoan-daca"
  },
  vawa: {
    en: "/practice-areas/immigration-services/vawa-petitions",
    es: "/es/areas-de-practica/servicios-de-inmigracion/peticiones-vawa",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/don-kien-vawa"
  },
  humanitarianParole: {
    en: "/practice-areas/immigration-services/humanitarian-parole-tps",
    es: "/es/areas-de-practica/servicios-de-inmigracion/parole-humanitario-tps",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/tai-ngoai-nhan-dao-tps"
  },
  greenCardRenewal: {
    en: "/practice-areas/immigration-services/green-card-renewal",
    es: "/es/areas-de-practica/servicios-de-inmigracion/renovacion-tarjeta-verde",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/gia-han-the-xanh"
  },
  consularProcessing: {
    en: "/practice-areas/immigration-services/consular-processing",
    es: "/es/areas-de-practica/servicios-de-inmigracion/procesamiento-consular",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/xu-ly-lanh-su"
  },
  uVisa: {
    en: "/practice-areas/immigration-services/u-visa",
    es: "/es/areas-de-practica/servicios-de-inmigracion/visa-u",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/visa-u"
  },
  advanceParole: {
    en: "/practice-areas/immigration-services/advance-parole",
    es: "/es/areas-de-practica/servicios-de-inmigracion/permiso-anticipado",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/phep-truoc"
  },
  reentryPermit: {
    en: "/practice-areas/immigration-services/re-entry-permit",
    es: "/es/areas-de-practica/servicios-de-inmigracion/permiso-reentrada",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/giay-phep-nhap-canh-lai"
  },
  eb5: {
    en: "/practice-areas/immigration-services/eb-5-investor-visa",
    es: "/es/areas-de-practica/servicios-de-inmigracion/visa-inversionista-eb5",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/visa-dau-tu-eb5"
  },
  studentVisas: {
    en: "/practice-areas/immigration-services/student-visas",
    es: "/es/areas-de-practica/servicios-de-inmigracion/visas-estudiante",
    vi: "/vi/linh-vuc-hanh-nghe/dich-vu-di-tru/visa-du-hoc"
  }
};

// Other pages
export const generalRoutes: Record<string, RouteMapping> = {
  home: {
    en: "/",
    es: "/es",
    vi: "/vi"
  },
  about: {
    en: "/about-us",
    es: "/es/acerca-de-nosotros",
    vi: "/vi/gioi-thieu"
  },
  contact: {
    en: "/contact",
    es: "/es/contacto",
    vi: "/vi/lien-he"
  },
  consultation: {
    en: "/booking",
    es: "/es/reservar",
    vi: "/vi/dat-lich"
  },
  booking: {
    en: "/booking",
    es: "/es/reservar",
    vi: "/vi/dat-lich"
  },
  blog: {
    en: "/blog",
    es: "/es/blog",
    vi: "/vi/blog"
  },
  practiceAreas: {
    en: "/practice-areas",
    es: "/es/areas-de-practica",
    vi: "/vi/linh-vuc-hanh-nghe"
  }
};

// Combine all routes
export const allRoutes = {
  ...generalRoutes,
  ...practiceAreaRoutes,
  ...immigrationRoutes
};

// Import extended routes
import { allExtendedRoutes } from "./route-mapping-extended";

// Combine core and extended routes
export const allRoutesComplete = {
  ...allRoutes,
  ...allExtendedRoutes
};

/**
 * Get translated URL for a given route key and target language
 */
export function getTranslatedUrl(routeKey: string, language: Language): string {
  const route = allRoutesComplete[routeKey as keyof typeof allRoutesComplete];
  if (!route) {
    console.warn(`Route key "${routeKey}" not found in route mapping`);
    return "/";
  }
  return route[language];
}

/**
 * Get route key from current URL path
 */
export function getRouteKeyFromPath(pathname: string): string | null {
  for (const [key, mapping] of Object.entries(allRoutesComplete)) {
    if (mapping.en === pathname || mapping.es === pathname || mapping.vi === pathname) {
      return key;
    }
  }
  return null;
}

/**
 * Get language from URL path
 */
export function getLanguageFromPath(pathname: string): Language {
  if (pathname.startsWith('/es')) return 'es';
  if (pathname.startsWith('/vi')) return 'vi';
  return 'en';
}

/**
 * Get current language URL from mapping
 */
export function getCurrentLanguageUrl(pathname: string, language: Language): string {
  const routeKey = getRouteKeyFromPath(pathname);
  if (routeKey) {
    return getTranslatedUrl(routeKey, language);
  }
  return pathname;
}
